import {
    resolveStockIconUrl,
    resolveStockLogoUrl,
    buildStockIconUrlFromSymbol,
    buildDefaultStockIconUrl,
} from 'utils/stockLogoUtils';

const VIEW_W = 900;
const VIEW_H = 520;
const PAD = 1.5;
const AVATAR_RASTER_SIZE = 128;

function escapeXml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * El SVG compartido se rasteriza dentro de un <img>, contexto que no descarga
 * subrecursos externos: los avatares deben viajar embebidos como data URI.
 */
function urlToCircularDataUri(url) {
    return new Promise((resolve) => {
        if (!url) {
            resolve('');
            return;
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
            try {
                const size = AVATAR_RASTER_SIZE;
                const canvas = document.createElement('canvas');
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');

                ctx.beginPath();
                ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
                ctx.closePath();
                ctx.clip();

                ctx.fillStyle = '#1e293b';
                ctx.fillRect(0, 0, size, size);

                const iw = img.naturalWidth || size;
                const ih = img.naturalHeight || size;
                const scale = Math.max(size / iw, size / ih);
                const dw = iw * scale;
                const dh = ih * scale;
                ctx.drawImage(img, (size - dw) / 2, (size - dh) / 2, dw, dh);

                resolve(canvas.toDataURL('image/png'));
            } catch (error) {
                resolve('');
            }
        };

        img.onerror = () => resolve('');
        img.src = url;
    });
}

async function resolveAvatarDataUri(cell) {
    const item = { ...(cell.item || {}), symbol: cell.symbol };
    const candidates = [
        resolveStockIconUrl(item),
        resolveStockLogoUrl(item),
        buildStockIconUrlFromSymbol(cell.symbol),
        buildDefaultStockIconUrl(),
    ].filter(Boolean);

    const tried = new Set();
    for (const url of candidates) {
        if (tried.has(url)) {
            continue;
        }
        tried.add(url);
        // eslint-disable-next-line no-await-in-loop
        const dataUri = await urlToCircularDataUri(url);
        if (dataUri) {
            return dataUri;
        }
    }

    return '';
}

/**
 * Pre-carga los avatares de cada símbolo como data URI (mapa symbol -> data URI).
 */
export async function loadHeatmapAvatars(cells) {
    const unique = [];
    const seen = new Set();
    (cells || []).forEach((cell) => {
        if (cell?.symbol && !seen.has(cell.symbol)) {
            seen.add(cell.symbol);
            unique.push(cell);
        }
    });

    const resolved = await Promise.all(unique.map((cell) => resolveAvatarDataUri(cell)));

    return unique.reduce((acc, cell, index) => {
        if (resolved[index]) {
            acc[cell.symbol] = resolved[index];
        }
        return acc;
    }, {});
}

/**
 * Build a shareable heatmap SVG showing symbol + % only (no $ P/L).
 */
export function buildShareHeatmapSvg(cells, options = {}) {
    const width = options.width || VIEW_W;
    const height = options.height || VIEW_H;
    const title = options.title || 'Mapa de Calor';
    const subtitle = options.subtitle || '';
    const avatars = options.avatars || {};

    const headerH = 54;
    const mapY = headerH;
    const mapH = height - headerH;

    const parts = [
        `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
        `<rect width="${width}" height="${height}" fill="#0f172a"/>`,
        `<text x="24" y="28" fill="#e2e8f0" font-size="20" font-weight="700" font-family="Arial, Helvetica, sans-serif">${escapeXml(title)}</text>`,
    ];

    if (subtitle) {
        parts.push(
            `<text x="24" y="46" fill="#94a3b8" font-size="12" font-family="Arial, Helvetica, sans-serif">${escapeXml(subtitle)}</text>`,
        );
    }

    // Scale cells into map area below header
    const scaleX = width / VIEW_W;
    const scaleY = mapH / VIEW_H;

    (cells || []).forEach((cell) => {
        const x = cell.x * scaleX + PAD;
        const y = mapY + cell.y * scaleY + PAD;
        const w = Math.max(0, cell.width * scaleX - PAD * 2);
        const h = Math.max(0, cell.height * scaleY - PAD * 2);
        const showPct = w > 56 && h > 36;
        const showSymbol = w > 32 && h > 22;
        const pctText = `${Number(cell.sharePct || 0).toFixed(1)}%`;

        parts.push(
            `<rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${w.toFixed(2)}" height="${h.toFixed(2)}" rx="4" ry="4" fill="${cell.color}"/>`,
        );

        const avatarUri = avatars[cell.symbol];
        const minSide = Math.min(w, h);
        const showAvatar = Boolean(avatarUri) && minSide >= 64;
        const avatarSize = showAvatar
            ? Math.max(24, Math.min(72, Math.floor(minSide * 0.36)))
            : 0;
        const gap = showAvatar ? 6 : 0;
        const lineH = 14;
        const textLines = (showSymbol ? 1 : 0) + (showPct ? 1 : 0);
        const contentH = avatarSize + (showAvatar && textLines ? gap : 0) + textLines * lineH;
        const contentTop = y + Math.max(4, (h - contentH) / 2);
        let textY = contentTop + avatarSize + gap + 11;

        if (showAvatar) {
            parts.push(
                `<image x="${(x + (w - avatarSize) / 2).toFixed(2)}" y="${contentTop.toFixed(2)}" width="${avatarSize}" height="${avatarSize}" href="${avatarUri}" xlink:href="${avatarUri}" preserveAspectRatio="xMidYMid slice"/>`,
            );
        }

        if (showSymbol) {
            parts.push(
                `<text x="${(x + w / 2).toFixed(2)}" y="${textY.toFixed(2)}" text-anchor="middle" fill="#f8fafc" font-size="13" font-weight="700" font-family="Arial, Helvetica, sans-serif">${escapeXml(cell.symbol)}</text>`,
            );
            textY += lineH;
        }
        if (showPct) {
            parts.push(
                `<text x="${(x + w / 2).toFixed(2)}" y="${textY.toFixed(2)}" text-anchor="middle" fill="rgba(248,250,252,0.9)" font-size="12" font-weight="600" font-family="Arial, Helvetica, sans-serif">${escapeXml(pctText)}</text>`,
            );
        }
    });

    parts.push('</svg>');
    return parts.join('');
}

export function svgStringToPngBlob(svgString, width = VIEW_W, height = VIEW_H) {
    return new Promise((resolve, reject) => {
        const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const img = new Image();

        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = '#0f172a';
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);
                URL.revokeObjectURL(url);
                canvas.toBlob((pngBlob) => {
                    if (!pngBlob) {
                        reject(new Error('png_failed'));
                        return;
                    }
                    resolve(pngBlob);
                }, 'image/png');
            } catch (error) {
                URL.revokeObjectURL(url);
                reject(error);
            }
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('svg_load_failed'));
        };

        img.src = url;
    });
}

export const HEATMAP_EXPORT_SIZE = {
    width: VIEW_W,
    height: VIEW_H,
};
